import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Box, CircularProgress, Alert } from '@mui/material';
import useChartData from '../hooks/useChartData';
import SharedTooltip from './SharedTooltip';
import { AXIS_STYLE, GRID_STYLE } from '../constants/ChartConfig';

const TeamEloChart = ({ selectedTeams = [] }) => {
  const { data: rawResults, loading, error } = useChartData(selectedTeams, (id) => `/api/team/${id}/elo-history`);

  const seriesData = useMemo(() => rawResults.map(res => ({
    name: res.team.teamName,
    color: res.team.color,
    data: (res.rawData.history || []).map(point => {
      const d = new Date(point.date);
      return {
        numericX: d.getFullYear() + d.getMonth() / 12 + (d.getDate() - 1) / (31 * 12),
        dateStr: point.date.split(' ')[0],
        [res.team.teamName]: point.eloAfter,
        matchInfo: point
      };
    }).sort((a, b) => a.numericX - b.numericX)
  })), [rawResults]);

  const xDomain = useMemo(() => {
    const allX = seriesData.flatMap(s => s.data.map(p => p.numericX));
    return allX.length ? [Math.min(...allX), Math.max(...allX)] : [0, 0];
  }, [seriesData]);

  const monthTicks = useMemo(() => {
    const ticks = [];
    for (let y = Math.floor(xDomain[0]); y <= Math.ceil(xDomain[1]); y++) {
      for (let m = 0; m < 12; m++) {
        const val = y + m / 12;
        if (val >= xDomain[0] && val <= xDomain[1]) 
          ticks.push({ value: val, label: `${String(m + 1).padStart(2, '0')}/${String(y).slice(-2)}` });
      }
    }
    return ticks;
  }, [xDomain]);

  if (loading && seriesData.length === 0) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 5 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: '100%', height: 480 }}>
      <ResponsiveContainer>
        <LineChart margin={{ top: 10, right: 30, left: 0, bottom: 60 }}>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis 
            {...AXIS_STYLE} 
            type="number" 
            dataKey="numericX" 
            domain={xDomain} 
            ticks={monthTicks.map(t => t.value)}
            tickFormatter={(val) => monthTicks.find(t => Math.abs(t.value - val) < 0.001)?.label || ''} 
          />
          <YAxis {...AXIS_STYLE} domain={['auto', 'auto']} angle={0} textAnchor="end" height={0} />
          <Tooltip content={<SharedTooltip />} shared />
          {seriesData.map(s => (
            <Line key={s.name} name={s.name} data={s.data} dataKey={s.name} stroke={s.color} strokeWidth={3} type="monotone" dot={{ r: 2 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default TeamEloChart;